/**
 * The loader's console log of runtime events, with the `surface:css-rejected` cap (layered shell
 * surfaces, R-90, R-96).
 *
 * The runtime emits one `surface:css-rejected` event per construct the stylesheet scoper drops
 * (R-90). One 1 MB stylesheet of `@x;` gave 350,000 events in 116 ms, and 350,000 console calls
 * stall the tab. R-96 puts the cap here, in the consumer, so the runtime's contract stays as R-90
 * wrote it.
 */
import type { RuntimeEvent } from '@zion/sdk-surfaces/v1';

/** How many `surface:css-rejected` events the log writes per stylesheet before it counts the rest (R-96). */
export const CSS_REJECTED_LOG_LIMIT = 20;

/** The prefix of every event line; the event itself is the line's second argument. */
export const RUNTIME_EVENT_LOG_PREFIX = '[shell] surface runtime event';

/** Where the log writes. `boot.ts` passes a function that calls `console.warn` at the time of the call. */
export type RuntimeEventLogSink = (...args: unknown[]) => void;

/** The count line for one stylesheet whose burst passed the cap. Names the mount id and two counts; no CSS text, no URL. */
export function cssRejectedSummary(mountId: string, total: number): string {
  return `[shell] ${total - CSS_REJECTED_LOG_LIMIT} more surface:css-rejected events from mount ${mountId} not logged; ${total} in all`;
}

/**
 * Build the listener `boot.ts` hands the loader host as `onRuntimeEvent`.
 *
 * - Any event other than `surface:css-rejected` writes one line, `sink(RUNTIME_EVENT_LOG_PREFIX,
 *   event)`, every time. Runtime events carry no value, payload, URL or body (WS-2), so the
 *   console may show them.
 * - `surface:css-rejected` events count per `mountId`. The runtime reports every drop of one
 *   stylesheet from the one mount whose fetch injected it, in one synchronous run, before the
 *   surface runs (R-90), so one mount id and one run is one stylesheet. The first
 *   {@link CSS_REJECTED_LOG_LIMIT} events of a run write one line each, as above. The rest write
 *   nothing. After the run ends (one microtask later), a run of more than the limit writes one
 *   line, {@link cssRejectedSummary}, with the number not written and the total. The count for that
 *   mount then starts again from zero, so the map of counts holds only mounts in a run.
 *
 * Never throws when `sink` does not throw. Holds no reference to an event after its call.
 */
export function createRuntimeEventLog(sink: RuntimeEventLogSink): (event: RuntimeEvent) => void {
  /** The open run of each mount: the events counted since its first one. A run closes one microtask after it opens. */
  const runs = new Map<string, { count: number }>();

  return (event) => {
    if (event.name !== 'surface:css-rejected') {
      sink(RUNTIME_EVENT_LOG_PREFIX, event);
      return;
    }
    const { mountId } = event;
    let run = runs.get(mountId);
    if (run === undefined) {
      const opened = { count: 0 };
      runs.set(mountId, opened);
      queueMicrotask(() => {
        runs.delete(mountId);
        if (opened.count > CSS_REJECTED_LOG_LIMIT) sink(cssRejectedSummary(mountId, opened.count));
      });
      run = opened;
    }
    run.count += 1;
    if (run.count <= CSS_REJECTED_LOG_LIMIT) sink(RUNTIME_EVENT_LOG_PREFIX, event);
  };
}
